import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  static values = {
    data: Array,
    positive: { type: Boolean, default: true }
  }

  connect() {
    const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

    const labels = this.dataValue.map(d => d.month)
    // Normalize to currency units (reais) to avoid SVG floating-point overflow
    const values = this.dataValue.map(d => Math.round(d.amount_cents) / 100)

    const hasPositive = values.some(v => v > 0)
    const hasNegative = values.some(v => v < 0)
    const isMixed = hasPositive && hasNegative

    const GREEN = '#198754'
    const RED = '#dc3545'
    // Color based on whether the overall data series is positive or negative.
    // isMixed will override colors anyway; for uniform series use majority sign.
    const color = hasNegative && !hasPositive ? RED : GREEN

    const options = {
      chart: {
        type: 'area',
        height: '100%',
        width: '100%',
        sparkline: { enabled: true },
        animations: { enabled: true },
      },
      series: [{ data: values }],
      labels: labels,
      // Let ApexCharts manage its own Y scale to avoid SVG Bezier overflow.
      // Only pin the axis to include zero when data is mixed so the zero
      // crossing renders correctly.
      yaxis: isMixed ? { min: Math.min(0, ...values), max: Math.max(0, ...values) } : {},
      stroke: {
        curve: 'monotoneCubic',
        width: 1.5,
      },
      fill: {
        type: 'solid',
        opacity: 0.06,
      },
      colors: [color],
      plotOptions: isMixed ? { area: { fillTo: 'origin' } } : {},
      markers: {
        size: 3,
        hover: { size: 4 },
      },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const v = values[dataPointIndex]
          const dotColor = v >= 0 ? GREEN : RED
          const formatted = (v > 0 ? '+' : '') + brl.format(v)
          return `<div style="padding:6px 10px;font-size:12px;line-height:1.6;background:var(--color-base-100);border:1px solid var(--color-base-300);color:var(--color-base-content);border-radius:var(--radius-box)">` +
            `<div><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${dotColor};margin-right:6px"></span>${labels[dataPointIndex]}</div>` +
            `<div style="font-weight:600;padding-left:14px;color:${dotColor}">${formatted}</div>` +
            `</div>`
        }
      },
    }

    if (isMixed) {
      // Per-point dot colors: green for positive, red for negative
      options.markers.discrete = values.map((v, i) => ({
        seriesIndex: 0,
        dataPointIndex: i,
        fillColor: v >= 0 ? GREEN : RED,
        strokeColor: v >= 0 ? GREEN : RED,
        size: 2,
      }))

      options.colors = [GREEN]
      options.plotOptions = { area: { fillTo: 'origin' } }

      // Apply vertical SVG gradients to both stroke and fill paths so that
      // the line and area change color at the zero crossing point.
      const applyGradients = () => {
        const svg = this.element.querySelector('svg')
        if (!svg) return

        const allVals = [...values, 0]
        const top = Math.max(...allVals)
        const bottom = Math.min(...allVals)
        const zeroPct = top === bottom ? 50 : (top / (top - bottom)) * 100

        const defs = svg.querySelector('defs') || svg.insertBefore(document.createElementNS('http://www.w3.org/2000/svg', 'defs'), svg.firstChild)
        const uid = Math.random().toString(36).slice(2, 8)

        const makeGrad = (id, stops) => {
          const existing = svg.querySelector(`#${id}`)
          if (existing) existing.remove()
          const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient')
          grad.setAttribute('id', id)
          grad.setAttribute('x1', '0'); grad.setAttribute('y1', '0')
          grad.setAttribute('x2', '0'); grad.setAttribute('y2', '1')
          for (const s of stops) {
            const stop = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
            stop.setAttribute('offset', s.offset)
            stop.setAttribute('stop-color', s.color)
            stop.setAttribute('stop-opacity', s.opacity ?? '1')
            grad.appendChild(stop)
          }
          defs.appendChild(grad)
          return id
        }

        // Stroke gradient: sharp color change at zero
        const strokeGradId = makeGrad(`sp-stroke-${uid}`, [
          { offset: '0%',          color: GREEN },
          { offset: `${zeroPct}%`, color: GREEN },
          { offset: `${zeroPct}%`, color: RED },
          { offset: '100%',        color: RED },
        ])

        // Fill gradient: same position, semi-transparent
        const fillGradId = makeGrad(`sp-fill-${uid}`, [
          { offset: '0%',          color: GREEN, opacity: '0.15' },
          { offset: `${zeroPct}%`, color: GREEN, opacity: '0.05' },
          { offset: `${zeroPct}%`, color: RED,   opacity: '0.05' },
          { offset: '100%',        color: RED,   opacity: '0.15' },
        ])

        for (const p of svg.querySelectorAll('.apexcharts-area-series path')) {
          if (p.getAttribute('fill') === 'none') {
            p.setAttribute('stroke', `url(#${strokeGradId})`)
          } else {
            p.setAttribute('fill', `url(#${fillGradId})`)
          }
        }
      }

      options.fill = { type: 'solid', opacity: 0.08 }
      options.chart.events = {
        mounted: applyGradients,
        updated: applyGradients,
      }
    }

    this.chart = new ApexCharts(this.element, options)
    this.chart.render()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }
}
